import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { TrendingUp, Settings as SettingsIcon } from 'lucide-react';
import { Settings } from './components/Settings';
import { useSettings } from './context/SettingsContext';

function App() {
  // Get URL parameters for embed mode
  const params = new URLSearchParams(window.location.search);
  const isEmbed = params.get('embed') === 'true';
  
  const [showSettings, setShowSettings] = useState(false);
  const [acquisition, setAcquisition] = useState(
    Number(params.get('acquisition')) || 20
  );
  const [retention, setRetention] = useState(
    Number(params.get('retention')) || 85
  );
  const [shareOfWallet, setShareOfWallet] = useState(
    Number(params.get('shareOfWallet')) || 0
  );
  const { settings } = useSettings();

  const data = useMemo(() => {
    const quarters = 16;
    const data = [];
    const baseCustomers = settings.initialCustomers;
    const baseRevenue = settings.monthlyRevenue;
    const baselineRetention = 85; // Starting retention rate

    for (let i = 0; i < quarters; i++) {
      // Calculate baseline scenario with 85% retention
      let baselineCustomers = baseCustomers;
      for (let q = 0; q <= i; q++) {
        const newCustomers = acquisition;
        const retainedCustomers = Math.floor((baselineCustomers * baselineRetention) / 100);
        baselineCustomers = retainedCustomers + newCustomers;
      }
      const baseQuarterlyRevenue = baselineCustomers * baseRevenue * 3;
      
      // Calculate with retention improvements
      let customersWithRetention = baseCustomers;
      for (let q = 0; q <= i; q++) {
        const newCustomers = acquisition;
        const retainedCustomers = Math.floor((customersWithRetention * retention) / 100);
        customersWithRetention = retainedCustomers + newCustomers;
      }
      const revenueWithRetention = customersWithRetention * baseRevenue * 3;
      
      // Only show retention bonus if retention is above baseline (85%)
      // If retention is below 85%, adjust the base revenue down
      let adjustedBaseRevenue, retentionBonus;
      if (retention >= baselineRetention) {
        adjustedBaseRevenue = baseQuarterlyRevenue;
        retentionBonus = revenueWithRetention - baseQuarterlyRevenue;
      } else {
        adjustedBaseRevenue = revenueWithRetention;
        retentionBonus = 0;
      }
      
      // Calculate with share of wallet improvements
      const cumulativeShareOfWallet = Math.pow(1 + (shareOfWallet / 100), i + 1);
      const revenuePerCustomerWithWallet = baseRevenue * cumulativeShareOfWallet;
      const totalRevenueWithWallet = customersWithRetention * revenuePerCustomerWithWallet * 3;
      const walletBonus = totalRevenueWithWallet - revenueWithRetention;

      const date = new Date();
      date.setMonth(date.getMonth() + (i * 3));
      const quarterLabel = `Q${Math.floor(date.getMonth() / 3) + 1} '${date.getFullYear().toString().slice(-2)}`;

      data.push({
        quarter: quarterLabel,
        baseRevenue: adjustedBaseRevenue,
        retentionBonus: retentionBonus,
        walletBonus: walletBonus,
        totalRevenue: totalRevenueWithWallet,
        isLast: i === quarters - 1, // Flag to identify the last bar
      });
    }
    return data;
  }, [acquisition, retention, shareOfWallet, settings]);

  const maxRevenue = useMemo(() => {
    return Math.max(...data.map(d => d.totalRevenue));
  }, [data]);

  const yAxisDomain = useMemo(() => {
    const autoAdjustThreshold = settings.yAxisMaxValue * 0.9; // Auto-adjust when revenue exceeds 90% of max
    return [0, maxRevenue > autoAdjustThreshold ? 'auto' : settings.yAxisMaxValue];
  }, [maxRevenue, settings.yAxisMaxValue]);

  const formatRevenue = (value: number) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    }
    return `$${(value / 1000).toFixed(0)}K`;
  };

  // Custom label component that only shows for the last bar
  const renderCustomLabel = (props: any) => {
    const { x, y, width, payload } = props;
    const isLast = payload?.isLast;
    if (!isLast) return null;
    
    return (
      <text
        x={x + width / 2}
        y={y - 15}
        fill="#1e40af"
        textAnchor="middle"
        fontSize="12"
      >
        {formatRevenue(payload.totalRevenue)}
      </text>
    );
  };

  if (showSettings) {
    return (
      <div className="min-h-screen bg-black p-8">
        <div className="max-w-6xl mx-auto">
          <Settings 
            onBack={() => setShowSettings(false)} 
            currentState={{
              acquisition,
              retention,
              shareOfWallet,
              ...settings
            }}
          />
        </div>
      </div>
    );
  }

  const chartContent = (
    <div className={`${isEmbed ? 'h-full' : 'h-[400px]'} w-full relative`}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 40, right: 80, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="quarter" />
          <YAxis 
            domain={yAxisDomain}
            tickFormatter={formatRevenue} 
            ticks={[0, 250000, 500000, 750000, 1000000, 1250000]}
          />
          <Tooltip 
            formatter={(value: number, name: string) => {
              const labels = {
                baseRevenue: 'Base Revenue',
                retentionBonus: 'Retention Bonus',
                walletBonus: 'Share of Wallet Bonus'
              };
              return [formatRevenue(value), labels[name as keyof typeof labels] || name];
            }}
            labelFormatter={(label) => `Quarter: ${label}`}
          />
          <Bar dataKey="baseRevenue" stackId="revenue" fill="#9ca3af" />
          <Bar dataKey="retentionBonus" stackId="revenue" fill="#3b82f6" />
          <Bar dataKey="walletBonus" stackId="revenue" fill="#1e40af" />
        </BarChart>
      </ResponsiveContainer>
      
      {/* Overlay annotation */}
      {(() => {
        const lastDataPoint = data[data.length - 1];
        const totalAdditionalRevenue = lastDataPoint.retentionBonus + lastDataPoint.walletBonus;
        
        if (!totalAdditionalRevenue) return null;
        
        // Calculate vertical positions based on the chart's coordinate system
        const maxValue = yAxisDomain[1] === 'auto' ? maxRevenue : yAxisDomain[1];
        
        // Calculate where the additional revenue segments start and end
        const totalRevenue = lastDataPoint.totalRevenue;
        const baseRevenue = lastDataPoint.baseRevenue;
        
        // Convert to percentages of chart height (accounting for chart margins)
        const chartAreaHeight = 85; // Approximate chart area as percentage of total height
        const chartAreaTop = 10; // Approximate top margin as percentage
        
        // Top of additional revenue is at the total revenue
        // Bottom of additional revenue is at the base revenue (where blue segments start)
        const topOfAdditionalRevenue = totalRevenue / maxValue;
        const bottomOfAdditionalRevenue = baseRevenue / maxValue;
        
        // Calculate positions from top of container
        const topPosition = chartAreaTop + (1 - topOfAdditionalRevenue) * chartAreaHeight;
        const bottomPosition = chartAreaTop + (1 - (baseRevenue / maxValue)) * chartAreaHeight;
        const height = bottomPosition - topPosition;
        
        return (
          <div 
            className="absolute pointer-events-none"
            style={{
              right: '55px',
              top: `${topPosition}%`,
              height: `${height}%`,
            }}
          >
            {/* Curly brace - scales with height */}
            <div className="relative h-full">
              <svg 
                width="20" 
                height="100%"
                className="overflow-visible"
                viewBox={`0 0 25 ${height * 4}`}
                preserveAspectRatio="none"
              >
                <path
                  d={`M 3 3 Q 10 3 10 10 L 10 ${height * 1.6} Q 10 ${height * 2} 17 ${height * 2} Q 10 ${height * 2} 10 ${height * 2.4} L 10 ${height * 4 - 10} Q 10 ${height * 4 - 3} 3 ${height * 4 - 3}`}
                  stroke="#374151"
                  strokeWidth="1.5"
                  fill="none"
                />
              </svg>
              
              {/* Text - fixed size, positioned at center */}
              <div 
                className="absolute left-5 transform -translate-y-1/2"
                style={{ top: '50%' }}
              >
                <div className="text-sm font-semibold text-gray-700">
                  +{formatRevenue(totalAdditionalRevenue)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Additional Revenue
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );

  if (isEmbed) {
    return (
      <div className="h-full w-full p-4">
        {chartContent}
      </div>
    );
  }

  const controlsContent = (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          New Customers per Quarter: {acquisition}
        </label>
        <input
          type="range"
          min="0"
          max="40"
          value={acquisition}
          onChange={(e) => setAcquisition(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Customer Retention Rate: {retention}%
        </label>
        <input
          type="range"
          min="60"
          max="100"
          value={retention}
          onChange={(e) => setRetention(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Additional Share of Wallet: {shareOfWallet}%
        </label>
        <input
          type="range"
          min="0"
          max="20"
          value={shareOfWallet}
          onChange={(e) => setShareOfWallet(Number(e.target.value))}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
        />
      </div>

      {/* Legend */}
      <div className="border-t pt-4 mt-2">
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-800 rounded-sm"></div>
            <span className="text-gray-700">Increased Share of Wallet</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-blue-500 rounded-sm"></div>
            <span className="text-gray-700">Increased Retention</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-400 rounded-sm"></div>
            <span className="text-gray-700">Baseline</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <div className="w-full">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-800">Revenue Growth Calculator</h1>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <SettingsIcon className="w-4 h-4" />
              Settings
            </button>
          </div>
          
          <div className="flex flex-col sm:flex-row sm:gap-8">
            <div className="flex-1 mb-8 sm:mb-0 min-w-0">
              {chartContent}
            </div>

            <div className="w-full sm:w-[220px] flex-shrink-0">
              {controlsContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;